<template>
    <div
        v-if="loading || !benefits.length"
        class="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 px-6 py-16 flex items-center justify-center min-h-[240px]"
    >
        <ChartStatePlaceholder :loading="loading" />
    </div>

    <!-- Flat View -->
    <div v-else-if="!isGrouped" class="flex flex-col gap-4">
        <article
            v-for="row in benefits"
            :key="`${row.id}-${row.connected_account_id}`"
            class="w-full p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm flex flex-col gap-4"
            :class="locked ? 'cursor-default' : 'cursor-pointer'"
            @click="!locked && openDetails(row.item)"
        >
            <BenefitMobileRow :row="row" :locked="locked" :show-card="true" />
        </article>
    </div>

    <!-- Grouped By Card -->
    <div v-else class="flex flex-col gap-4">
        <article
            v-for="group in groups"
            :key="group.key"
            class="w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700"
        >
            <!-- Card header -->
            <div class="flex items-start gap-3 p-4">
                <!-- Card image with stack effect when multiple benefits -->
                <div class="relative w-12 h-8 shrink-0">
                    <div class="relative w-full h-full">
                        <div
                            v-if="group.rows.length > 1"
                            class="absolute w-[80%] h-full rounded-sm bg-slate-200 dark:bg-slate-700 -bottom-0.5 left-1/2 z-0 -translate-x-1/2"
                        ></div>
                        <div
                            v-if="group.rows.length > 1"
                            class="absolute w-[90%] h-full rounded-sm bg-slate-300 dark:bg-slate-600 -bottom-0.5 left-1/2 z-10 -translate-x-1/2"
                        ></div>
                        <ImageLoader class="relative z-20 w-full h-full">
                            <template #image>
                                <img
                                    v-if="
                                        group.card_image?.path &&
                                        group.card_image?.file_name
                                    "
                                    class="w-full h-full rounded-sm border border-slate-200 dark:border-slate-700 object-cover relative z-20"
                                    :src="`${cdnUrl}/${group.card_image.path}/_small/${group.card_image.file_name}`"
                                    :alt="group.card_name"
                                />
                            </template>
                            <template #error>
                                <div
                                    class="flex items-center justify-center w-full h-full rounded-sm bg-slate-200 dark:bg-slate-700"
                                >
                                    <Icon icon="credit-card" class="h-4 w-4" />
                                </div>
                            </template>
                        </ImageLoader>
                    </div>

                    <!-- Benefits count badge -->
                    <div
                        v-if="group.rows.length > 1"
                        class="absolute -right-2 -top-2 bg-slate-800 text-white text-xs font-medium rounded-full h-5 min-w-[20px] px-1.5 flex items-center justify-center z-20"
                    >
                        {{ group.rows.length }}
                    </div>
                </div>

                <div class="flex-1 min-h-8 min-w-0">
                    <h3
                        class="text-slate-800 dark:text-slate-100 text-base font-medium leading-tight"
                    >
                        <span class="block">
                            <span class="line-clamp-2 break-words">
                                {{ group.card_name }}
                                <span
                                    v-if="
                                        insightsShowCardNumbers &&
                                        group.card_last_4
                                    "
                                    class="text-slate-500 dark:text-slate-400 text-sm font-normal"
                                >
                                    ({{ group.card_last_4 }})
                                </span>
                            </span>
                        </span>
                    </h3>
                    <div
                        v-if="group.annual_total > 0"
                        class="py-1 px-2 bg-slate-100 dark:bg-slate-700 rounded-lg w-fit mt-1"
                    >
                        <p class="text-slate-500 dark:text-slate-400 font-medium text-sm">
                            Up to {{ formatMoney(group.annual_total) }}/year
                        </p>
                    </div>
                </div>

                <button
                    class="btn-base btn-icon"
                    aria-label="Toggle benefit group"
                    @click="toggleGroup(group.key)"
                >
                    <Icon
                        :icon="
                            isExpanded(group.key)
                                ? 'chevron-up'
                                : 'chevron-down'
                        "
                        class="w-5 h-5"
                    />
                </button>
            </div>

            <!-- Aggregate usage / locked upgrade -->
            <div v-if="locked" class="px-4 pb-4">
                <button
                    type="button"
                    class="flex w-full items-center justify-center gap-2 py-3 px-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer"
                    @click.stop="openUpgrade"
                >
                    <Icon icon="lock" class="w-4 h-4 text-slate-500 dark:text-slate-400" />
                    <span>Upgrade to track</span>
                </button>
            </div>
            <div v-else class="px-4 pb-2">
                <div
                    class="flex items-center text-sm text-slate-800 dark:text-slate-100 font-normal"
                >
                    <span>{{ formatMoney(group.used) }}</span>
                    <span class="ml-1">of {{ formatMoney(group.total) }}</span>
                </div>
                <div class="mt-1.5">
                    <div class="w-full rounded-full h-1 relative bg-orange-200">
                        <div
                            class="bg-10x-orange h-1 rounded-full transition-all"
                            :style="{ width: clamp(group.utilization) + '%' }"
                        ></div>
                        <div
                            v-if="Number(group.utilization) >= 100"
                            class="absolute -right-1 top-1/2 -translate-y-1/2 w-4 h-4 bg-10x-orange rounded-full flex items-center justify-center border-2 border-white"
                        >
                            <Icon
                                icon="check"
                                svgClass="w-2 h-2 text-white"
                                class="text-white"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Expanded children with tree connector -->
            <div
                v-if="isExpanded(group.key)"
                class="divide-y-2 divide-slate-100 dark:divide-slate-700 pl-11 pr-4 pb-1"
            >
                <div
                    v-for="(row, idx) in group.rows"
                    :key="`${group.key}-${row.id}-${row.connected_account_id}`"
                    class="relative pt-4 pb-1"
                    :class="locked ? 'cursor-default' : 'cursor-pointer'"
                    @click="!locked && openDetails(row.item)"
                >
                    <!-- Tree SVG connectors -->
                    <div
                        class="absolute w-16 -left-16 -top-2 bottom-0 h-20 pointer-events-none"
                    >
                        <svg
                            v-if="idx === 0"
                            width="12"
                            height="75"
                            viewBox="0 0 12 75"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            class="absolute -top-4 left-12"
                        >
                            <path
                                d="M1.5 5V53Q1.5 66 12 66"
                                stroke="#CBD5E1"
                                stroke-width="1"
                                stroke-linecap="round"
                            />
                        </svg>
                        <svg
                            v-else
                            width="12"
                            height="190"
                            viewBox="0 0 12 190"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            class="absolute -top-24 left-12"
                        >
                            <path
                                d="M1.5 0V137Q1.5 150 12 150"
                                stroke="#CBD5E1"
                                stroke-width="1"
                                stroke-linecap="round"
                            />
                        </svg>
                    </div>

                    <BenefitMobileRow
                        :row="row"
                        :locked="locked"
                        :show-card="false"
                        :hide-utilization="true"
                    />
                </div>
            </div>
        </article>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import Icon from '@/components/ui/icon/icon.vue';
import ChartStatePlaceholder from './chart-state-placeholder.vue';
import BenefitMobileRow from './insights-table-row-mobile.vue';

defineProps({
    benefits: { type: Array, required: true },
});

const store = useCardBenefitsStore();
const {
    insightsGroupBy,
    insightsShowCardNumbers,
    insightsBenefitsGrouped,
    loading,
    isSubscribed,
} = storeToRefs(store);
const cdnUrl = computed(() => store.cdnUrl);
const locked = computed(() => !isSubscribed.value);
const openUpgrade = () => store.setShowUpgradeModal(true);

const isGrouped = computed(() => insightsGroupBy.value === 'by_card');
const groups = computed(() => insightsBenefitsGrouped.value.groups || []);

const expanded = ref(new Set());
watch(groups, (newGroups) => {
    const validKeys = new Set(newGroups.map((g) => g.key));
    const next = new Set();
    expanded.value.forEach((k) => {
        if (validKeys.has(k)) next.add(k);
    });
    expanded.value = next;
});

const isExpanded = (key) => expanded.value.has(key);
const toggleGroup = (key) => {
    const next = new Set(expanded.value);
    if (next.has(key)) {
        next.delete(key);
    } else {
        next.add(key);
    }
    expanded.value = next;
};

const clamp = (n) => Math.max(0, Math.min(100, Number(n) || 0));

const formatMoney = (value) => {
    if (!value && value !== 0) return '$0';
    return '$' + Math.round(Number(value)).toLocaleString('en-US');
};

const openDetails = (item) => {
    if (!item) return;
    store.openBenefitDetails(item);
};
</script>
